import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { endpoints, baseUrl } from '../../../api/api';

interface StepData {
  key: string;
  label: string;
  stuck: number;
  reached: number;
}

interface DropoffData {
  totalBooks: number;
  completedBooks: number;
  steps: StepData[];
}

type ViewMode = 'stuck' | 'reach';

function pct(num: number, den: number): string {
  if (!den) return '0%';
  return `${Math.round((num / den) * 100)}%`;
}

const WizardDropoff: React.FC = () => {
  const [data, setData]       = useState<DropoffData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const [view, setView]       = useState<ViewMode>('stuck');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(false);
      try {
        const token = localStorage.getItem('accessToken');
        const res   = await axios.get(`${baseUrl}${endpoints.adminGetWizardDropoff}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const maxStuck  = data ? Math.max(...data.steps.map(s => s.stuck),  1) : 1;
  const maxReach  = data ? Math.max(...data.steps.map(s => s.reached), 1) : 1;

  const worstStepIdx = data
    ? data.steps.reduce((best, s, i) => s.stuck > data.steps[best].stuck ? i : best, 0)
    : -1;

  return (
    <div className="px-5 py-4 flex-1 min-w-0">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-gray-400">
          {view === 'stuck' ? 'Draft books currently stuck at each step' : 'Books that reached each step or further'}
        </p>
        <div className="flex gap-1">
          {(['stuck', 'reach'] as ViewMode[]).map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                view === v ? 'bg-gray-800 text-white' : 'text-gray-500 hover:bg-gray-200'
              }`}
            >
              {v === 'stuck' ? 'Stuck' : 'Reach'}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-24">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400" />
        </div>
      )}

      {error && !loading && (
        <p className="text-xs text-red-400 text-center py-6">Failed to load drop-off data</p>
      )}

      {data && !loading && (
        <div className="space-y-2.5">
          {data.steps.map((step, i) => {
            const isStuck = view === 'stuck';
            const value   = isStuck ? step.stuck : step.reached;
            const max     = isStuck ? maxStuck : maxReach;
            const denom   = isStuck ? (data.totalBooks - data.completedBooks) : data.totalBooks;
            const barPct  = max > 0 ? (value / max) * 100 : 0;
            const isWorst = isStuck && i === worstStepIdx && step.stuck > 0;

            return (
              <div key={step.key}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-medium ${isWorst ? 'text-red-600' : 'text-gray-700'}`}>
                    {step.label}
                    {isWorst && (
                      <span className="ml-1.5 text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-semibold">
                        bottleneck
                      </span>
                    )}
                  </span>
                  <span className={`text-xs font-semibold ${isWorst ? 'text-red-600' : 'text-gray-600'}`}>
                    {value.toLocaleString()} <span className="text-gray-400 font-normal">({pct(value, denom)})</span>
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      isWorst ? 'bg-red-400' : isStuck ? 'bg-amber-400' : 'bg-blue-400'
                    }`}
                    style={{ width: `${barPct}%` }}
                  />
                </div>
              </div>
            );
          })}

          <div className="pt-2 mt-1 border-t border-gray-200 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Completed (submitted / approved / rejected)
            </span>
            <span className="text-xs font-semibold text-green-600">
              {data.completedBooks.toLocaleString()}{' '}
              <span className="text-gray-400 font-normal">({pct(data.completedBooks, data.totalBooks)})</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default WizardDropoff;